import { create } from "zustand"

import type {
  GeocodeV4Response,
  HomeSessionV2,
  Invoice,
  Userdata,
} from "@be-miniapp/api"
import type { BottomBars } from "@be-miniapp/types"

interface UserSessionLocation {
  latitude: number | undefined
  longitude: number | undefined
}

interface CreateUserSessionStoreParams {
  initialLocation: UserSessionLocation
  removeAccessToken: () => void
}

interface UserSession {
  access_token: string | null
  is_logged_in: boolean
  user_data?: Userdata
  location: UserSessionLocation
  home_sessions?: HomeSessionV2
  bottom_bars?: BottomBars
  currentAddress?: GeocodeV4Response
  invoice?: Invoice
  setToken: (token: string | null) => void
  setUserAddress: (address: GeocodeV4Response | undefined) => void
  setUserdata: (data: Userdata) => void
  updateUserdata: (data: Partial<Userdata>) => void
  setHomeSessions: (data: HomeSessionV2) => void
  setBottomBars: (data: BottomBars) => void
  setLocation: (latitude: number, longitude: number) => void
  setInvoice: (invoice?: Invoice) => void
  logout: () => void
}

function createUserSessionStore({
  initialLocation,
  removeAccessToken,
}: CreateUserSessionStoreParams) {
  return create<UserSession>((set) => ({
    is_logged_in: false,
    access_token: null,
    location: {
      latitude: initialLocation.latitude,
      longitude: initialLocation.longitude,
    },
    invoice: undefined,
    setToken: (token: string | null) => set({ access_token: token }),
    setUserAddress: (address: GeocodeV4Response | undefined) =>
      set({ currentAddress: address }),
    setUserdata: (data: Userdata) => set({ user_data: data }),
    updateUserdata: (data: Partial<Userdata>) =>
      set((state) => ({
        user_data: {
          ...(state.user_data ?? ({} as Userdata)),
          ...data,
        },
      })),
    setHomeSessions: (data: HomeSessionV2) => set({ home_sessions: data }),
    setBottomBars: (data: BottomBars) => set({ bottom_bars: data }),
    setLocation: (latitude: number, longitude: number) => {
      set({ location: { latitude, longitude } })
    },
    setInvoice: (invoice?: Invoice) => set({ invoice }),
    logout: () => {
      set({
        is_logged_in: false,
        access_token: "",
        user_data: undefined,
        invoice: undefined,
      })
      removeAccessToken()
    },
  }))
}

export { createUserSessionStore }
export type { UserSession, UserSessionLocation }
